import { GUIDES } from "./catalog";
import type { HelpDialKind, HelpGuide } from "./types";

/** Lowercase alphanumeric tokens. */
function tokens(s: string): string[] {
  return s.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

// Dropped from queries so "how do I add a file" scores on "add" + "file", not the filler words.
const STOP = new Set(["how", "do", "i", "to", "a", "an", "the", "my", "in", "on", "of", "can", "help", "me", "with", "this", "is", "for"]);

/**
 * Rank the how-to catalog against a natural-language "how do I…" query. Query tokens are matched against
 * each guide's title, summary and keywords; exact token hits score higher than substring hits. An empty
 * (or all-stopword) query returns every guide in catalog order, so the launcher can show the full list.
 *
 * `dialKind` is the kind of dial currently open, if any. When one is, guides written for the other kind
 * are dropped — they would spotlight controls that aren't there. When it's null (the dashboard, groups,
 * settings) nothing is dropped: we don't know which dial the user has in mind, and quietly hiding half
 * the answers to "how do I change a status" is worse than showing both, badged.
 */
export function searchGuides(query: string, dialKind: HelpDialKind | null = null): HelpGuide[] {
  const available = dialKind ? GUIDES.filter((g) => !g.dialKind || g.dialKind === dialKind) : GUIDES;
  const qs = tokens(query).filter((t) => !STOP.has(t));
  if (qs.length === 0) {
    return [...available];
  }
  const scored = available.map((guide) => {
    const haystack = [guide.title, guide.summary, ...guide.keywords].join(" ").toLowerCase();
    const words = new Set(tokens(haystack));
    let score = 0;
    for (const q of qs) {
      if (words.has(q)) {
        score += 2;
      } else if (haystack.includes(q)) {
        score += 1;
      }
    }
    return { guide, score };
  }).filter((x) => x.score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.guide);
}
